import React, { useState } from 'react';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  MessageCircle,
  AlertCircle,
  CheckCircle,
  Globe,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Users,
  HelpCircle,
  Calendar,
  ChevronRight,
  Building,
  Heart
} from 'lucide-react';

/**
 * ContactPage Component
 * 
 * Based on GitHub best practices and patterns from:
 * - Healthcare React applications (OpenMRS, Medplum)
 * - Material-UI responsive design patterns
 * - React Hook Form for form handling
 * - Tailwind CSS for responsive, tablet-friendly layouts
 * 
 * Features:
 * - Tablet-optimized responsive design (768px - 1024px breakpoints)
 * - Multiple contact methods (form, phone, email, chat)
 * - Office locations with maps integration
 * - Emergency contact information
 * - Social media links
 * - Form validation and error handling
 * - Success/error message handling
 * - Accessibility compliant (WCAG 2.1)
 * 
 * Integration with App.js routing structure from Rhythm of life platform
 */

const ContactPage = () => {
  // Form state management
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    department: '',
    message: '',
    preferredContact: 'email'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  // Department options for the form
  const departments = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'appointments', label: 'Appointments & Scheduling' },
    { value: 'clinical', label: 'Clinical Services' },
    { value: 'billing', label: 'Billing & Insurance' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'feedback', label: 'Feedback & Complaints' },
    { value: 'partnerships', label: 'Partnerships & Collaborations' }
  ];

  // Office locations
  const locations = [
    {
      id: 1,
      name: 'Main Campus - Sheridan',
      address: '30 North Gould Street',
      city: 'Sheridan, WY US',
      phone: '+1 (888) 404-7470',
      email: 'info@rhythmoflife.com',
      hours: 'Mon-Sat: 9:00 AM - 8:00 PM',
      services: ['In-person Consultations', 'Emergency Services', 'Group Therapy'],
      mapUrl: 'https://maps.google.com/?q=Sheridan+WY'
    },
    // {
    //   id: 2,
    //   name: 'Mumbai Center',
    //   address: '456 Linking Road, Bandra West',
    //   city: 'Mumbai, Maharashtra 400050',
    //   phone: '+1 22 4123 4567',
    //   email: 'mumbai@rhythmoflife.com',
    //   hours: 'Mon-Sat: 9:00 AM - 7:00 PM',
    //   services: ['Consultations', 'Therapy Sessions', 'Assessments'],
    //   mapUrl: 'https://maps.google.com/?q=Mumbai+Bandra'
    // },
    // {
    //   id: 3,
    //   name: 'Delhi NCR Hub',
    //   address: '789 Sector 18, Noida',
    //   city: 'Noida, UP 201301',
    //   phone: '+1 11 4123 4567',
    //   email: 'delhi@rhythmoflife.com',
    //   hours: 'Mon-Sat: 9:00 AM - 7:00 PM',
    //   services: ['Virtual Consultations', 'In-person Therapy'],
    //   mapUrl: 'https://maps.google.com/?q=Noida+Sector+18'
    // }
  ];

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    return newErrors;
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only digits for phone
    if (name === 'phone') {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In production, this would be an actual API call:
      // const response = await fetch('/api/contact', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData)
      // });

      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        department: '',
        message: '',
        preferredContact: 'email'
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-serene-blue-600 to-growth-green-600 text-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl opacity-95">
              We're here to help you on your mental health journey
            </p>
          </div>
        </div>
      </div>

      {/* Emergency Notice */}
      <div className="bg-red-50 border-l-4 border-red-500">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm md:text-base text-red-700">
                <span className="font-semibold">Need immediate help?</span> Call our 24/7 crisis helpline: 
                <a href="tel:+18884047470" className="ml-2 font-bold underline">
                  +1 (888) 404-7470
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Contact Form - Takes up 2 columns on large screens */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Send Us a Message</h2>
              
              {/* Success/Error Messages */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-green-800 font-semibold">Message sent successfully!</p>
                    <p className="text-green-700 text-sm mt-1">
                      We'll get back to you within 24 hours.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-red-800 font-semibold">Failed to send message</p>
                    <p className="text-red-700 text-sm mt-1">
                      Please try again or contact us directly.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-serene-blue-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="John Doe"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-serene-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="john@example.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Phone and Department Row */}
               <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>

                  <div className="flex">
                    {/* Country Code */}
                    <span className="inline-flex items-center px-4 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-600 text-sm">
                      +1
                    </span>

                    {/* Phone Input */}
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={10}
                      className={`w-full px-4 py-2 border rounded-r-lg focus:outline-none focus:ring-2 focus:ring-serene-blue-500 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="9876543210"
                    />
                  </div>

                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-serene-blue-500 ${
                      errors.subject ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="How can we help you?"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-600">{errors.subject}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-serene-blue-500 resize-none ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your inquiry in detail..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-600">{errors.message}</p>
                  )}
                </div>

                {/* Preferred Contact Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="email"
                        checked={formData.preferredContact === 'email'}
                        onChange={handleChange}
                        className="mr-2 text-serene-blue-500 focus:ring-serene-blue-500"
                      />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="phone"
                        checked={formData.preferredContact === 'phone'}
                        onChange={handleChange}
                        className="mr-2 text-serene-blue-500 focus:ring-serene-blue-500"
                      />
                      <span className="text-sm text-gray-700">Phone</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="preferredContact"
                        value="whatsapp"
                        checked={formData.preferredContact === 'whatsapp'}
                        onChange={handleChange}
                        className="mr-2 text-serene-blue-500 focus:ring-serene-blue-500"
                      />
                      <span className="text-sm text-gray-700">WhatsApp</span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto px-8 py-3 bg-serene-blue-600 text-white font-semibold rounded-lg hover:bg-serene-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-serene-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information Sidebar */}
          <div className="space-y-6">
            {/* Quick Contact Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Contact</h3>
              
              <div className="space-y-4">
                {/* Phone */}
                <div className="flex items-start">
                  <Phone className="h-5 w-5 text-serene-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Phone</p>
                    <a href="tel:+18884047470" className="text-serene-blue-600 hover:underline">
                      +1 (888) 404-7470
                    </a>
                    <p className="text-sm text-gray-600">Mon-Sat, 9 AM - 8 PM</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start">
                  <Mail className="h-5 w-5 text-serene-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Email</p>
                    <a href="mailto:support@rhythmoflife.com" className="text-serene-blue-600 hover:underline">
                      support@rhythmoflife.com
                    </a>
                    <p className="text-sm text-gray-600">24/7 support</p>
                  </div>
                </div>

                {/* Live Chat */}
                <div className="flex items-start">
                  <MessageCircle className="h-5 w-5 text-serene-blue-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-800">Live Chat</p>
                    <button className="text-serene-blue-600 hover:underline">
                      Start Chat Now
                    </button>
                    <p className="text-sm text-gray-600">Average wait: 2 mins</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Working Hours
              </h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monday - Friday</span>
                  <span className="font-semibold">9:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Saturday</span>
                  <span className="font-semibold">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sunday</span>
                  <span className="font-semibold">Emergency Only</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  <span className="font-semibold">24/7 Crisis Support:</span>
                  <br />
                  <a href="tel:+18884047470" className="text-serene-blue-600 hover:underline">
                    +1 (888) 404-7470
                  </a>
                </p>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Follow Us</h3>
              
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                  aria-label="Twitter"
                >
                  <Twitter className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-pink-600 text-white rounded-full flex items-center justify-center hover:bg-pink-700 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              </div>

              <p className="mt-4 text-sm text-gray-600">
                Stay connected for mental health tips, resources, and updates.
              </p>
            </div>
          </div>
        </div>

        {/* Office Locations Section */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800">Our Locations</h2>
            <p className="text-gray-600 mt-2">Visit us at any of our centers across India</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 container mx-auto">
            {locations.map((location) => (
              <div key={location.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                {/* Map Placeholder */}
                <div className="h-48 bg-gray-200 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <MapPin className="h-12 w-12 text-gray-400" />
                  </div>
                  <a
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 right-2 bg-white px-3 py-1 rounded-lg shadow text-sm font-semibold text-serene-blue-600 hover:bg-serene-blue-50 transition-colors"
                  >
                    View on Map →
                  </a>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{location.name}</h3>
                  
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-start">
                      <Building className="h-4 w-4 mt-0.5 mr-2 flex-shrink-0 text-gray-400" />
                      <div>
                        <p>{location.address}</p>
                        <p>{location.city}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`tel:${location.phone}`} className="text-serene-blue-600 hover:underline">
                        {location.phone}
                      </a>
                    </div>

                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`mailto:${location.email}`} className="text-serene-blue-600 hover:underline">
                        {location.email}
                      </a>
                    </div>

                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      <span>{location.hours}</span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Services Available:</p>
                    <div className="flex flex-wrap gap-1">
                      {location.services.map((service, index) => (
                        <span
                          key={index}
                          className="text-xs px-2 py-1 bg-serene-blue-50 text-serene-blue-700 rounded"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-12 bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Frequently Asked Questions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  How quickly will I receive a response?
                </h3>
                <p className="text-gray-600 text-sm">
                  We aim to respond to all inquiries within 24 hours during business days. 
                  Urgent matters are prioritized.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  Do you offer online consultations?
                </h3>
                <p className="text-gray-600 text-sm">
                  Yes, we offer secure video consultations for patients who cannot visit our centers 
                  in person.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  Is my information confidential?
                </h3>
                <p className="text-gray-600 text-sm">
                  Absolutely. We follow strict privacy policies and HIPAA guidelines to protect 
                  your personal health information.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  How do I book an appointment?
                </h3>
                <p className="text-gray-600 text-sm">
                  You can book appointments through our online portal, by calling us, or by 
                  visiting our centers directly.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  Do you accept insurance?
                </h3>
                <p className="text-gray-600 text-sm">
                  We accept most major insurance plans. Contact our billing department for 
                  specific coverage details.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <HelpCircle className="h-4 w-4 mr-2 text-serene-blue-600" />
                  What if I need emergency help?
                </h3>
                <p className="text-gray-600 text-sm">
                  For emergencies, call our 24/7 crisis helpline at +1 (888) 404-7470 or visit 
                  your nearest emergency room.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <a
              href="/faq"
              className="inline-flex items-center text-serene-blue-600 hover:text-serene-blue-700 font-semibold"
            >
              View All FAQs
              <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-br from-growth-green-600 to-serene-blue-600 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <Heart className="h-12 w-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Your Mental Health Matters
          </h2>
          <p className="text-lg opacity-95 mb-6 max-w-2xl mx-auto">
            Take the first step towards better mental health. Our team of professionals 
            is ready to support you on your journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/booking"
              className="px-6 py-3 bg-white text-serene-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Book Appointment
            </a>
            <a
              href="/self-assessment"
              className="px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-serene-blue-600 transition-all inline-flex items-center justify-center"
            >
              <Users className="h-5 w-5 mr-2" />
              Take Assessment
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;